import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getApplicationOfferPackEmailPreview } from '@/lib/api';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, RefreshCw } from 'lucide-react';

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function cleanError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(date);
}

function lastSentText(lastSent) {
  if (!lastSent?.sent_at) return '';
  const names = safeList(lastSent.document_names);
  return `Emailed on ${formatDateTime(lastSent.sent_at)} to ${lastSent.to || 'student'}${names.length ? ` (${names.join(', ')})` : ''}`;
}

export default function OfferPackEmailCard({ application, onApplicationUpdated }) {
  const applicationId = application?.id || application?.application_id;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPreview = useCallback(async ({ quiet = false } = {}) => {
    if (!applicationId || application?.status !== 'offered') return null;
    setLoading(true);
    if (!quiet) setError('');
    try {
      const { data } = await getApplicationOfferPackEmailPreview(applicationId);
      setPreview(data);
      return data;
    } catch (err) {
      console.error(err);
      const message = cleanError(err, 'Document email details could not be loaded.');
      setError(message);
      if (!quiet) toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [application?.status, applicationId]);

  useEffect(() => {
    loadPreview({ quiet: true });
    const refresh = () => loadPreview({ quiet: true });
    window.addEventListener('portal-documents-changed', refresh);
    return () => window.removeEventListener('portal-documents-changed', refresh);
  }, [loadPreview]);

  if (application?.status !== 'offered') return null;

  const attachments = safeList(preview?.attachments);
  const recipient = {
    ...application,
    id: applicationId,
    email: preview?.recipient || application?.email || '',
    name: `${application?.first_name || ''} ${application?.last_name || ''}`.trim(),
  };

  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            <Mail className="h-4 w-4" /> Email Documents
          </CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={() => loadPreview()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        {preview?.last_sent?.sent_at && (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {lastSentText(preview.last_sent)}
          </p>
        )}
        {loading && !preview ? (
          <div className="flex items-center gap-2 py-4 text-sm text-[#062B1A]/55">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading email options...
          </div>
        ) : attachments.length ? (
          <PortalEmailComposer
            audience="applications"
            recipients={[recipient]}
            buttonLabel="Email documents to student"
            buttonClassName="w-full bg-[#062B1A] text-white hover:bg-[#041B10]"
            disabled={loading}
            offerPack={{
              ...preview,
              applicationId,
            }}
            onSent={(result) => {
              setPreview((current) => ({
                ...(current || {}),
                last_sent: result?.last_sent || current?.last_sent,
              }));
              if (result?.application) onApplicationUpdated?.(result.application);
              window.dispatchEvent(new Event('portal-documents-changed'));
            }}
          />
        ) : (
          <p className="rounded-lg bg-[#F7F8FA] px-3 py-2 text-sm text-[#062B1A]/60">
            No admission documents are ready to email.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
